import { useState, useEffect } from 'react';
import { XMarkIcon, PlayIcon } from '@heroicons/react/24/outline';
import Editor from '@monaco-editor/react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { scriptEditorOptions } from '../../../components/MonacoConfig';
import { executeScriptWithERP } from '../scriptRuntime';

interface ScriptEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  componentId: string;
  componentLabel: string;
  script: string;
  onSave: (script: string) => void;
}

export default function ScriptEditorModal({
  isOpen,
  onClose,
  componentId,
  componentLabel,
  script,
  onSave,
}: ScriptEditorModalProps) {
  useEscapeKey(onClose, isOpen);
  const [code, setCode] = useState(script || '');
  const [testOutput, setTestOutput] = useState<string>('');
  const [testError, setTestError] = useState<string>('');

  useEffect(() => {
    setCode(script || getDefaultScript());
  }, [script, componentId]);

  if (!isOpen) return null;

  const getDefaultScript = () => `// Button click handler
// Available variables:
//   - event: The click event
//   - componentId: "${componentId}"
//   - document: DOM document
//   - console: For logging
//   - ERP: Database access (query/mutate)

console.log('Button "${componentLabel}" clicked!');

// Example: Load customers from masterdata service
// const data = await ERP.query('masterdata',
//   '{ customers { nodes { id name email } } }');
// console.log('Customers:', data.customers.nodes);

// Example: Create a new customer
// const result = await ERP.mutate('masterdata',
//   'mutation { createCustomer(input: { name: "New Corp", type: "Company" }) { id name } }');
// console.log('Created:', result.createCustomer);

// Example: Load orders from shop service
// const orders = await ERP.query('shop',
//   '{ orders { nodes { id orderNumber status } } }');
// console.log('Orders:', orders.orders.nodes);

// Example: Query through the gateway (federated)
// const data = await ERP.query('gateway',
//   '{ customers { nodes { id name } } }');

// Utility functions: ERP.round(), ERP.sum(), ERP.avg(),
//   ERP.isEmail(), ERP.isUUID(), ERP.now(), ERP.formatDate(),
//   ERP.slugify()
`;

  const handleTest = () => {
    setTestOutput('');
    setTestError('');
    
    const mockConsole = {
      log: (...args: unknown[]) => console.log('[Script Test]', ...args),
      warn: (...args: unknown[]) => console.warn('[Script Test]', ...args),
      error: (...args: unknown[]) => console.error('[Script Test]', ...args),
    };

    executeScriptWithERP(
      code,
      { id: componentId, props: { label: componentLabel } },
      { type: 'click', target: null },
      mockConsole,
      { allowDOM: false, allowFetch: false },
    )
      .then((logs) => {
        setTestOutput(logs.join('\n') || 'Script executed successfully (no output)');
      })
      .catch((err) => {
        setTestError(err instanceof Error ? err.message : 'Unknown error');
      });
  };

  const handleSave = () => {
    onSave(code);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold">
              Script Editor - {componentLabel}
            </h2>
            <p className="text-sm text-gray-500">
              Write JavaScript code that runs when this button is clicked
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-auto max-h-[calc(90vh-200px)]">
          <div className="mb-4">
            <label className="label mb-2 flex items-center justify-between">
              <span>JavaScript Code</span>
              <button
                onClick={handleTest}
                className="btn-secondary flex items-center gap-1 text-sm py-1 px-2"
              >
                <PlayIcon className="h-4 w-4" />
                Test
              </button>
            </label>
            <div className="rounded-md overflow-hidden border border-gray-700">
              <Editor
                height="300px"
                language="javascript"
                theme={document.documentElement.classList.contains('dark') ? 'vs-dark' : 'vs'}
                value={code}
                onChange={(value) => setCode(value || '')}
                options={scriptEditorOptions}
              />
            </div>
          </div>

          {/* Test Output */}
          {(testOutput || testError) && (
            <div className="mb-4">
              <label className="label mb-2">Test Output</label>
              <div className={`p-3 rounded-md font-mono text-sm ${
                testError 
                  ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}>
                <pre className="whitespace-pre-wrap">
                  {testError || testOutput}
                </pre>
              </div>
            </div>
          )}

          {/* Help */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-4">
            <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">
              Available APIs
            </h4>
            <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">event</code> - The click event object</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">componentId</code> - This button's ID</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">document</code> - DOM document for element manipulation</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">fetch()</code> - Make API calls</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">console.log()</code> - Log messages</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">alert()</code> - Show alerts</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">ERP.query(service, query, vars?)</code> - Query data from an ERP service</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">ERP.mutate(service, mutation, vars?)</code> - Write data to an ERP service</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">ERP.round() / ERP.sum() / ERP.avg()</code> - Math utilities</li>
              <li><code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">ERP.isEmail() / ERP.isUUID()</code> - Validation helpers</li>
            </ul>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
              Services: masterdata, shop, accounting, user, company, gateway. Scripts are async — use <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">await</code>.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary">
            Save Script
          </button>
        </div>
      </div>
    </div>
  );
}
